import { useState, useEffect, useRef, useCallback } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import ReactQuill from "react-quill-new";
import "react-quill-new/dist/quill.snow.css";
import { Plus, Search, Edit2, Trash2, Copy, Globe, FileText, Eye, EyeOff, ChevronRight, X, ExternalLink, Image as ImageIcon, Video, Monitor, Smartphone, Code2 } from "lucide-react";
import { toast } from "sonner";
import MediaUploadInput from "@/components/MediaUploadInput";
import { sanitizeHtmlRaw } from "@/components/tabs/ContentTab";
import { AboutUsEditor } from "@/components/templates/AboutUsTemplate";
import { ContactUsEditor } from "@/components/templates/ContactUsTemplate";
import { TermsOfUseEditor } from "@/components/templates/TermsOfUseTemplate";
import { ShippingPolicyEditor } from "@/components/templates/ShippingPolicyTemplate";
import { ReturnRefundEditor } from "@/components/templates/ReturnRefundTemplate";
import { CancellationPolicyEditor } from "@/components/templates/CancellationPolicyTemplate";

const API = process.env.REACT_APP_BACKEND_URL;

const PAGE_TYPES = ["Content", "Home", "Legal", "Policy", "Guide", "Landing", "FAQ", "Tool", "About", "Contact"];
const TEMPLATES = ["Default", "Full Width", "Sidebar Left", "Sidebar Right", "Minimal", "Landing", "About Us", "Contact Us", "Terms of Use", "Shipping Policy", "Return & Refund Policy", "Cancellation Policy"];
const STATUS_OPTIONS = ["published", "draft", "hidden"];

const statusStyle = {
  published: "bg-green-50 text-green-700 border border-green-200",
  draft: "bg-amber-50 text-amber-700 border border-amber-200",
  hidden: "bg-red-50 text-red-600 border border-red-200",
};

function slugify(s) {
  return s.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, "");
}

function formatDate(iso) {
  if (!iso) return "—";
  return new Date(iso).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" });
}

export default function PagesAdmin() {
  const [pages, setPages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [typeFilter, setTypeFilter] = useState("All Types");
  const [statusFilter, setStatusFilter] = useState("All Status");
  const [showForm, setShowForm] = useState(false);
  const [editPage, setEditPage] = useState(null);
  const token = localStorage.getItem("it_token");

  const fetchPages = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/api/admin/pages`, { headers: { Authorization: `Bearer ${token}` } });
      setPages(res.data);
    } catch {
      toast.error("Failed to load pages");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchPages(); }, []);

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this page?")) return;
    try {
      await axios.delete(`${API}/api/admin/pages/${id}`, { headers: { Authorization: `Bearer ${token}` } });
      toast.success("Page deleted");
      fetchPages();
    } catch { toast.error("Failed to delete"); }
  };

  const handleDuplicate = async (page) => {
    try {
      const dup = { ...page, title: `${page.title} (Copy)`, slug: `${page.slug}-copy`, status: "draft" };
      delete dup.id;
      await axios.post(`${API}/api/admin/pages`, dup, { headers: { Authorization: `Bearer ${token}` } });
      toast.success("Page duplicated");
      fetchPages();
    } catch { toast.error("Failed to duplicate"); }
  };

  const filtered = pages.filter(p => {
    const matchSearch = !search || p.title.toLowerCase().includes(search.toLowerCase()) || p.slug.toLowerCase().includes(search.toLowerCase());
    const matchType = typeFilter === "All Types" || p.page_type === typeFilter;
    const matchStatus = statusFilter === "All Status" || p.status === statusFilter;
    return matchSearch && matchType && matchStatus;
  });

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <div className="bg-white border-b border-slate-200 px-6 py-4">
        <div className="flex items-center justify-between">
          <div>
            <nav className="flex items-center gap-1.5 text-xs text-slate-400 mb-1">
              <span>Admin</span><ChevronRight className="w-3 h-3" /><span className="text-slate-700 font-medium">Pages</span>
            </nav>
            <h1 className="text-xl font-black text-slate-900">Pages</h1>
            <p className="text-xs text-slate-500 mt-0.5">Create and manage website pages like About, Contact, Terms, etc.</p>
          </div>
          <button
            onClick={() => { setEditPage(null); setShowForm(true); }}
            className="flex items-center gap-2 bg-orange-500 hover:bg-orange-600 text-white text-sm font-semibold px-4 py-2 rounded-xl transition-colors"
            data-testid="create-page-btn"
          >
            <Plus className="w-4 h-4" /> Create New Page
          </button>
        </div>
      </div>

      <div className="p-6">
        {/* Filter Bar */}
        <div className="flex items-center gap-3 mb-5 flex-wrap bg-white border border-slate-100 rounded-xl px-4 py-3">
          <div className="flex items-center gap-2 flex-1 min-w-48 bg-slate-50 rounded-lg px-3 py-2">
            <Search className="w-3.5 h-3.5 text-slate-400" />
            <input
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder="Search pages..."
              className="text-xs bg-transparent outline-none w-full text-slate-700 placeholder-slate-400"
              data-testid="pages-search"
            />
          </div>
          <select value={typeFilter} onChange={e => setTypeFilter(e.target.value)}
            className="text-xs border border-slate-200 rounded-lg px-3 py-2 bg-white text-slate-700 focus:outline-none focus:border-orange-400">
            <option>All Types</option>
            {PAGE_TYPES.map(t => <option key={t}>{t}</option>)}
          </select>
          <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)}
            className="text-xs border border-slate-200 rounded-lg px-3 py-2 bg-white text-slate-700 focus:outline-none focus:border-orange-400">
            <option>All Status</option>
            {STATUS_OPTIONS.map(s => <option key={s} className="capitalize">{s}</option>)}
          </select>
        </div>

        {/* Table */}
        <div className="bg-white rounded-xl border border-slate-100 overflow-hidden">
          <table className="w-full text-xs" data-testid="pages-table">
            <thead>
              <tr className="bg-slate-50 border-b border-slate-100">
                <th className="text-left px-4 py-3 font-semibold text-slate-500 w-8"><input type="checkbox" className="accent-orange-500" /></th>
                <th className="text-left px-4 py-3 font-semibold text-slate-500">Page Title</th>
                <th className="text-left px-4 py-3 font-semibold text-slate-500">URL Slug</th>
                <th className="text-left px-4 py-3 font-semibold text-slate-500">Page Type</th>
                <th className="text-left px-4 py-3 font-semibold text-slate-500">Template</th>
                <th className="text-left px-4 py-3 font-semibold text-slate-500">Last Updated</th>
                <th className="text-left px-4 py-3 font-semibold text-slate-500">Status</th>
                <th className="text-right px-4 py-3 font-semibold text-slate-500">Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                Array.from({ length: 5 }).map((_, i) => (
                  <tr key={i} className="border-b border-slate-50 animate-pulse">
                    {Array.from({ length: 8 }).map((_, j) => (
                      <td key={j} className="px-4 py-4"><div className="h-3 bg-slate-100 rounded w-full" /></td>
                    ))}
                  </tr>
                ))
              ) : filtered.length === 0 ? (
                <tr><td colSpan={8} className="px-4 py-12 text-center text-slate-400">No pages found</td></tr>
              ) : filtered.map(page => (
                <tr key={page.id} className="border-b border-slate-50 hover:bg-slate-50/50 transition-colors">
                  <td className="px-4 py-3"><input type="checkbox" className="accent-orange-500" /></td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-3">
                      <div className="w-9 h-9 rounded-lg bg-slate-100 flex items-center justify-center flex-shrink-0">
                        {page.thumbnail
                          ? <img src={page.thumbnail} alt="" className="w-9 h-9 rounded-lg object-cover" />
                          : <FileText className="w-4 h-4 text-slate-400" />
                        }
                      </div>
                      <div>
                        <p className="font-semibold text-slate-800 text-sm">{page.title}</p>
                        {page.seo_description && <p className="text-[11px] text-slate-400 line-clamp-1">{page.seo_description}</p>}
                      </div>
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <a href={`/pages/${page.slug}`} target="_blank" rel="noreferrer"
                      className="text-blue-600 hover:underline flex items-center gap-1 text-xs font-mono">
                      /pages/{page.slug} <ExternalLink className="w-2.5 h-2.5" />
                    </a>
                  </td>
                  <td className="px-4 py-3">
                    <span className="bg-slate-100 text-slate-600 rounded px-1.5 py-0.5 font-medium">{page.page_type}</span>
                  </td>
                  <td className="px-4 py-3 text-slate-500">{page.template || "Default"}</td>
                  <td className="px-4 py-3 text-slate-500">{formatDate(page.updated_at)}</td>
                  <td className="px-4 py-3">
                    <span className={`px-2 py-0.5 rounded-full text-[11px] font-semibold capitalize ${statusStyle[page.status] || statusStyle.draft}`}>
                      {page.status}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center justify-end gap-1">
                      <button onClick={() => { setEditPage(page); setShowForm(true); }}
                        className="p-1.5 rounded-lg text-slate-400 hover:text-orange-500 hover:bg-orange-50 transition-colors" title="Edit">
                        <Edit2 className="w-3.5 h-3.5" />
                      </button>
                      <button onClick={() => handleDuplicate(page)}
                        className="p-1.5 rounded-lg text-slate-400 hover:text-blue-500 hover:bg-blue-50 transition-colors" title="Duplicate">
                        <Copy className="w-3.5 h-3.5" />
                      </button>
                      <button onClick={() => handleDelete(page.id)}
                        className="p-1.5 rounded-lg text-slate-400 hover:text-red-500 hover:bg-red-50 transition-colors" title="Delete">
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {filtered.length > 0 && (
            <div className="px-4 py-3 border-t border-slate-100 flex items-center justify-between text-xs text-slate-400">
              <span>Showing {filtered.length} of {pages.length} pages</span>
            </div>
          )}
        </div>
      </div>

      {/* Create/Edit Form Modal */}
      {showForm && (
        <PageFormModal
          page={editPage}
          onClose={() => setShowForm(false)}
          onSaved={() => { setShowForm(false); fetchPages(); }}
          token={token}
        />
      )}
    </div>
  );
}

const QUILL_MODULES = {
  toolbar: [
    [{ header: [1, 2, 3, 4, false] }],
    ["bold", "italic", "underline", "strike"],
    [{ color: [] }, { background: [] }],
    [{ list: "ordered" }, { list: "bullet" }],
    [{ align: [] }],
    ["blockquote", "code-block"],
    ["link"],
    ["clean"],
  ],
};

const QUILL_FORMATS = [
  "header", "bold", "italic", "underline", "strike",
  "color", "background", "list", "align",
  "blockquote", "code-block", "link", "image",
];

function ImageUploadButton({ token, onInsert }) {
  const inputRef = useRef();
  const [uploading, setUploading] = useState(false);

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const res = await axios.post(`${process.env.REACT_APP_BACKEND_URL}/api/upload/image`, fd, {
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "multipart/form-data" },
      });
      onInsert(res.data.url);
      toast.success("Image inserted");
    } catch { toast.error("Upload failed"); }
    finally { setUploading(false); e.target.value = ""; }
  };

  return (
    <>
      <button type="button" disabled={uploading}
        onClick={() => inputRef.current?.click()}
        className="flex items-center gap-1.5 text-xs px-3 py-1.5 border border-slate-300 rounded-lg hover:bg-slate-50 disabled:opacity-50 transition-colors">
        <ImageIcon className="w-3.5 h-3.5 text-slate-500" />
        {uploading ? "Uploading…" : "Upload Image"}
      </button>
      <input ref={inputRef} type="file" accept="image/*" className="hidden" onChange={handleFile} />
    </>
  );
}

function VideoEmbedButton({ onEmbed }) {
  const [show, setShow] = useState(false);
  const [url, setUrl] = useState("");

  const handleEmbed = () => {
    if (!url.trim()) return;
    let embedUrl = url;
    // Convert YouTube watch URL to embed
    const ytMatch = url.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/)([a-zA-Z0-9_-]{11})/);
    if (ytMatch) embedUrl = `https://www.youtube.com/embed/${ytMatch[1]}`;
    // Convert Vimeo URL to embed
    const vimeoMatch = url.match(/vimeo\.com\/(\d+)/);
    if (vimeoMatch) embedUrl = `https://player.vimeo.com/video/${vimeoMatch[1]}`;

    const html = `<iframe src="${embedUrl}" frameborder="0" allowfullscreen style="width:100%;aspect-ratio:16/9;border-radius:8px;margin:8px 0;"></iframe>`;
    onEmbed(html);
    setUrl(""); setShow(false);
    toast.success("Video embedded");
  };

  return (
    <>
      <button type="button" onClick={() => setShow(true)}
        className="flex items-center gap-1.5 text-xs px-3 py-1.5 border border-slate-300 rounded-lg hover:bg-slate-50 transition-colors">
        <Video className="w-3.5 h-3.5 text-slate-500" />
        Embed Video
      </button>
      {show && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/40 p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-sm font-bold text-slate-900">Embed Video</h3>
              <button onClick={() => setShow(false)} className="text-slate-400 hover:text-slate-600"><X className="w-4 h-4" /></button>
            </div>
            <p className="text-xs text-slate-500 mb-3">Paste a YouTube or Vimeo URL and it will be converted to an embed automatically.</p>
            <input value={url} onChange={e => setUrl(e.target.value)} onKeyDown={e => e.key === "Enter" && handleEmbed()}
              placeholder="https://www.youtube.com/watch?v=..."
              className="w-full text-sm border border-slate-300 rounded-xl px-3 py-2.5 mb-4 focus:outline-none focus:border-orange-500" />
            <div className="flex gap-3">
              <button onClick={() => setShow(false)} className="flex-1 text-sm text-slate-500 hover:text-slate-700 py-2 rounded-xl border border-slate-200">Cancel</button>
              <button onClick={handleEmbed} className="flex-1 bg-orange-500 hover:bg-orange-600 text-white text-sm font-semibold py-2 rounded-xl transition-colors">Embed</button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

function PageFormModal({ page, onClose, onSaved, token }) {
  const isEdit = !!page;
  const [form, setForm] = useState({
    title: page?.title || "",
    slug: page?.slug || "",
    page_type: page?.page_type || "Content",
    template: page?.template || "Default",
    status: page?.status || "draft",
    content: page?.content || "",
    hero_desktop: page?.hero_desktop || "",
    hero_mobile: page?.hero_mobile || "",
    seo_title: page?.seo_title || "",
    seo_description: page?.seo_description || "",
    thumbnail: page?.thumbnail || "",
    template_data: page?.template_data || {},
    hide_title: page?.hide_title || false,
  });
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState("details");
  const [htmlMode, setHtmlMode] = useState(false);
  const [rawHtml, setRawHtml] = useState(page?.content || "");

  // Keep rawHtml in sync when form.content changes externally (e.g. page load)
  const toggleHtmlMode = () => {
    if (!htmlMode) {
      // switching TO html mode — seed textarea with current content
      setRawHtml(form.content || "");
    } else {
      // switching BACK to Quill — warn if content has <style> tags since Quill will strip them
      if (rawHtml.includes("<style") || rawHtml.includes("<!DOCTYPE") || rawHtml.includes("<html")) {
        toast.warning("This HTML has <style> tags. Switching to Visual will strip all CSS. Save directly from HTML mode instead.");
        return; // don't switch
      }
      const sanitized = sanitizeHtmlRaw(rawHtml);
      setForm(f => ({ ...f, content: sanitized }));
    }
    setHtmlMode(h => !h);
  };

  const update = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const handleTitleChange = (v) => {
    update("title", v);
    if (!isEdit || !form.slug) update("slug", slugify(v));
  };

  const handleSave = async () => {
    if (!form.title.trim()) { toast.error("Title is required"); return; }
    if (!form.slug.trim()) { toast.error("URL slug is required"); return; }
    setSaving(true);
    // When saving in HTML mode, always use the textarea's rawHtml value directly
    // to prevent any Quill-related onChange from overwriting the content.
    const payload = htmlMode ? { ...form, content: rawHtml } : form;
    try {
      if (isEdit) {
        await axios.put(`${process.env.REACT_APP_BACKEND_URL}/api/admin/pages/${page.id}`, payload, { headers: { Authorization: `Bearer ${token}` } });
        toast.success("Page updated");
      } else {
        await axios.post(`${process.env.REACT_APP_BACKEND_URL}/api/admin/pages`, payload, { headers: { Authorization: `Bearer ${token}` } });
        toast.success("Page created");
      }
      onSaved();
    } catch { toast.error("Failed to save page"); } finally { setSaving(false); }
  };

  const STRUCTURED_TEMPLATES = ["About Us", "Contact Us", "Terms of Use", "Shipping Policy", "Return & Refund Policy", "Cancellation Policy"];
  const tabs = [
    { id: "details", label: "Details" },
    { id: "media",   label: "Media" },
    { id: "seo",     label: "SEO" },
    { id: "content", label: STRUCTURED_TEMPLATES.includes(form.template) ? "Template Content" : "Content" },
  ];

  return (
    <div className="fixed inset-0 z-50 flex items-stretch bg-black/40">
      <div className="bg-white w-full max-w-5xl m-auto rounded-2xl shadow-2xl flex flex-col max-h-[96vh]">
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-100">
          <h2 className="text-base font-bold text-slate-900">{isEdit ? "Edit Page" : "Create New Page"}</h2>
          <button onClick={onClose} className="p-1.5 rounded-lg text-slate-400 hover:text-slate-700 hover:bg-slate-100">
            <X className="w-4 h-4" />
          </button>
        </div>
        <div className="flex gap-0 border-b border-slate-100 px-6">
          {tabs.map(t => (
            <button key={t.id} onClick={() => setActiveTab(t.id)}
              className={`text-xs font-semibold px-4 py-3 border-b-2 transition-colors ${activeTab === t.id ? "border-orange-500 text-orange-600" : "border-transparent text-slate-500 hover:text-slate-700"}`}>
              {t.label}
            </button>
          ))}
        </div>
        <div className="flex-1 overflow-y-auto p-6 space-y-4">
          {activeTab === "details" && (
            <>
              <div>
                <label className="block text-xs font-semibold text-slate-700 mb-1.5">Page Title *</label>
                <input value={form.title} onChange={e => handleTitleChange(e.target.value)}
                  placeholder="About Us" className="w-full text-sm border border-slate-200 rounded-xl px-3 py-2.5 focus:outline-none focus:border-orange-500" />
              </div>
              <div>
                <label className="block text-xs font-semibold text-slate-700 mb-1.5">URL Slug *</label>
                <div className="flex items-center gap-2 border border-slate-200 rounded-xl px-3 py-2.5 focus-within:border-orange-500">
                  <span className="text-slate-400 text-sm">/pages/</span>
                  <input value={form.slug} onChange={e => update("slug", slugify(e.target.value))}
                    placeholder="about-us" className="flex-1 text-sm outline-none" />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-semibold text-slate-700 mb-1.5">Page Type</label>
                  <select value={form.page_type} onChange={e => update("page_type", e.target.value)}
                    className="w-full text-sm border border-slate-200 rounded-xl px-3 py-2.5 focus:outline-none focus:border-orange-500">
                    {PAGE_TYPES.map(t => <option key={t}>{t}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-semibold text-slate-700 mb-1.5">Template</label>
                  <select value={form.template} onChange={e => update("template", e.target.value)}
                    className="w-full text-sm border border-slate-200 rounded-xl px-3 py-2.5 focus:outline-none focus:border-orange-500">
                    {TEMPLATES.map(t => <option key={t}>{t}</option>)}
                  </select>
                </div>
              </div>
              <div>
                <label className="block text-xs font-semibold text-slate-700 mb-1.5">Status</label>
                <div className="flex gap-2">
                  {STATUS_OPTIONS.map(s => (
                    <button key={s} onClick={() => update("status", s)}
                      className={`px-4 py-2 rounded-xl text-xs font-semibold capitalize transition-all ${form.status === s ? "bg-orange-500 text-white" : "bg-slate-100 text-slate-600 hover:bg-slate-200"}`}>
                      {s}
                    </button>
                  ))}
                </div>
              </div>
              <div className="flex items-center justify-between bg-slate-50 border border-slate-200 rounded-xl px-4 py-3">
                <div>
                  <p className="text-xs font-semibold text-slate-700">Hide Page Title on Storefront</p>
                  <p className="text-[11px] text-slate-400 mt-0.5">When on, the title won't appear or leave any space on the public page</p>
                </div>
                <button type="button" onClick={() => update("hide_title", !form.hide_title)}
                  className={`relative w-10 h-5 rounded-full transition-colors ${form.hide_title ? "bg-orange-500" : "bg-slate-300"}`}>
                  <span className={`absolute top-0.5 w-4 h-4 bg-white rounded-full shadow transition-transform ${form.hide_title ? "translate-x-5" : "translate-x-0.5"}`} />
                </button>
              </div>
            </>
          )}
          {activeTab === "media" && (
            <div className="space-y-6">
              <div className="bg-blue-50 border border-blue-200 rounded-xl p-3 flex items-start gap-2">
                <Monitor className="w-4 h-4 text-blue-500 flex-shrink-0 mt-0.5" />
                <p className="text-xs text-blue-700 leading-relaxed">
                  Upload separate media (image, GIF, or video) for <strong>desktop</strong> and <strong>mobile</strong>.
                  If mobile media is not uploaded, the desktop media will be shown on all devices.
                </p>
              </div>

              {/* Desktop media */}
              <div className="bg-white border border-slate-200 rounded-xl p-4">
                <div className="flex items-center gap-2 mb-3">
                  <div className="w-7 h-7 rounded-lg bg-indigo-100 flex items-center justify-center">
                    <Monitor className="w-3.5 h-3.5 text-indigo-600" />
                  </div>
                  <div>
                    <p className="text-xs font-bold text-slate-800">Desktop Media</p>
                    <p className="text-[10px] text-slate-400">Shown on screens wider than 768 px</p>
                  </div>
                </div>
                <MediaUploadInput
                  value={form.hero_desktop}
                  onChange={v => update("hero_desktop", v)}
                  placeholder="Upload or paste URL for desktop banner / hero…"
                />
                {form.hero_desktop && (
                  <div className="mt-3 rounded-xl overflow-hidden border border-slate-200 bg-slate-50 max-h-48 flex items-center justify-center">
                    {/\.(mp4|webm|mov|ogv)(\?.*)?$/i.test(form.hero_desktop) ? (
                      <video src={form.hero_desktop} controls className="max-h-48 w-full object-contain" />
                    ) : (
                      <img src={form.hero_desktop} alt="Desktop preview" className="max-h-48 w-full object-contain" />
                    )}
                  </div>
                )}
              </div>

              {/* Mobile media */}
              <div className="bg-white border border-slate-200 rounded-xl p-4">
                <div className="flex items-center gap-2 mb-3">
                  <div className="w-7 h-7 rounded-lg bg-pink-100 flex items-center justify-center">
                    <Smartphone className="w-3.5 h-3.5 text-pink-600" />
                  </div>
                  <div>
                    <p className="text-xs font-bold text-slate-800">Mobile Media</p>
                    <p className="text-[10px] text-slate-400">Shown on screens narrower than 768 px · if blank, desktop media is used</p>
                  </div>
                </div>
                <MediaUploadInput
                  value={form.hero_mobile}
                  onChange={v => update("hero_mobile", v)}
                  placeholder="Upload or paste URL for mobile banner / hero (optional)…"
                />
                {form.hero_mobile && (
                  <div className="mt-3 rounded-xl overflow-hidden border border-slate-200 bg-slate-50 max-h-48 flex items-center justify-center">
                    {/\.(mp4|webm|mov|ogv)(\?.*)?$/i.test(form.hero_mobile) ? (
                      <video src={form.hero_mobile} controls className="max-h-48 w-full object-contain" />
                    ) : (
                      <img src={form.hero_mobile} alt="Mobile preview" className="max-h-48 w-full object-contain" />
                    )}
                  </div>
                )}
                {!form.hero_mobile && form.hero_desktop && (
                  <div className="mt-2 flex items-center gap-1.5 text-[11px] text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-2.5 py-1.5">
                    <Smartphone className="w-3 h-3 flex-shrink-0" />
                    No mobile media — desktop media will be shown on mobile too.
                  </div>
                )}
              </div>
            </div>
          )}
          {activeTab === "seo" && (
            <>
              <div>
                <label className="block text-xs font-semibold text-slate-700 mb-1.5">SEO Title</label>
                <input value={form.seo_title} onChange={e => update("seo_title", e.target.value)}
                  placeholder="Same as page title if empty"
                  className="w-full text-sm border border-slate-200 rounded-xl px-3 py-2.5 focus:outline-none focus:border-orange-500" />
              </div>
              <div>
                <label className="block text-xs font-semibold text-slate-700 mb-1.5">Meta Description</label>
                <textarea value={form.seo_description} onChange={e => update("seo_description", e.target.value)}
                  rows={3} placeholder="Brief description for search engines (150-160 chars)"
                  className="w-full text-sm border border-slate-200 rounded-xl px-3 py-2.5 focus:outline-none focus:border-orange-500 resize-none" />
                <p className="text-[11px] text-slate-400 mt-1">{form.seo_description.length}/160 characters</p>
              </div>
              <div>
                <label className="block text-xs font-semibold text-slate-700 mb-1.5">Thumbnail Image URL</label>
                <input value={form.thumbnail} onChange={e => update("thumbnail", e.target.value)}
                  placeholder="https://..." className="w-full text-sm border border-slate-200 rounded-xl px-3 py-2.5 focus:outline-none focus:border-orange-500" />
              </div>
            </>
          )}
          {activeTab === "content" && (
            <div className="space-y-3">
              {form.template === "About Us" ? (
                <AboutUsEditor
                  data={form.template_data}
                  onChange={data => update("template_data", data)}
                />
              ) : form.template === "Contact Us" ? (
                <ContactUsEditor
                  data={form.template_data}
                  onChange={data => update("template_data", data)}
                />
              ) : form.template === "Terms of Use" ? (
                <TermsOfUseEditor
                  data={form.template_data}
                  onChange={data => update("template_data", data)}
                />
              ) : form.template === "Shipping Policy" ? (
                <ShippingPolicyEditor
                  data={form.template_data}
                  onChange={data => update("template_data", data)}
                />
              ) : form.template === "Return & Refund Policy" ? (
                <ReturnRefundEditor
                  data={form.template_data}
                  onChange={data => update("template_data", data)}
                />
              ) : form.template === "Cancellation Policy" ? (
                <CancellationPolicyEditor
                  data={form.template_data}
                  onChange={data => update("template_data", data)}
                />
              ) : (
                <>
                  <div className="flex items-center justify-between">
                    <label className="text-xs font-semibold text-slate-700">Page Content</label>
                    <div className="flex gap-2">
                      {!htmlMode && (
                        <>
                          <VideoEmbedButton onEmbed={html => setForm(f => ({ ...f, content: (f.content || "") + html }))} />
                          <ImageUploadButton
                            token={token}
                            onInsert={url => setForm(f => ({ ...f, content: (f.content || "") + `<img src="${url}" alt="" style="max-width:100%;border-radius:8px;margin:8px 0;" />` }))}
                          />
                        </>
                      )}
                      <button
                        onClick={toggleHtmlMode}
                        title={htmlMode ? "Switch to Visual Editor" : "Switch to HTML Mode"}
                        className={`flex items-center gap-1.5 text-xs font-medium px-3 py-1.5 rounded-lg border transition-colors ${
                          htmlMode
                            ? "bg-orange-500 text-white border-orange-500"
                            : "text-slate-600 border-slate-200 hover:bg-slate-50"
                        }`}
                      >
                        <Code2 className="w-3.5 h-3.5" />
                        {htmlMode ? "Visual" : "HTML"}
                      </button>
                    </div>
                  </div>

                  {htmlMode ? (
                    <>
                      <textarea
                        value={rawHtml}
                        onChange={e => {
                          setRawHtml(e.target.value);
                          setForm(f => ({ ...f, content: e.target.value }));
                        }}
                        spellCheck={false}
                        placeholder="Paste or write raw HTML here..."
                        className="w-full font-mono text-xs text-slate-800 bg-slate-950 text-green-400 border border-slate-200 rounded-xl px-4 py-3 resize-none focus:outline-none focus:border-orange-400"
                        style={{ minHeight: 480 }}
                      />
                      <p className="text-[11px] text-slate-400">HTML mode — paste a full layout. <code className="bg-slate-100 text-slate-600 px-1 rounded">&lt;script&gt;</code> and <code className="bg-slate-100 text-slate-600 px-1 rounded">on*</code> handlers are always stripped for security. If your HTML has <code className="bg-slate-100 text-slate-600 px-1 rounded">&lt;style&gt;</code> tags, <strong>save directly from this mode</strong> — switching to Visual will remove all CSS.</p>
                    </>
                  ) : (
                    <>
                      <div className="border border-slate-200 rounded-xl overflow-hidden quill-custom">
                        <ReactQuill
                          theme="snow"
                          value={form.content}
                          onChange={v => setForm(f => ({ ...f, content: v }))}
                          modules={QUILL_MODULES}
                          formats={QUILL_FORMATS}
                          style={{ minHeight: 480 }}
                          placeholder="Start writing your page content here..."
                        />
                      </div>
                      <p className="text-[11px] text-slate-400">Tip: Use the toolbar to format text, add images, embed videos (YouTube/Vimeo), insert links and more.</p>
                    </>
                  )}
                </>
              )}
            </div>
          )}
        </div>
        <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-slate-100">
          <button onClick={onClose} className="text-sm text-slate-500 hover:text-slate-700 font-medium px-4 py-2">Cancel</button>
          <button onClick={handleSave} disabled={saving}
            className="bg-orange-500 hover:bg-orange-600 disabled:opacity-50 text-white text-sm font-semibold px-6 py-2 rounded-xl transition-colors">
            {saving ? "Saving..." : (isEdit ? "Save Changes" : "Create Page")}
          </button>
        </div>
      </div>
    </div>
  );
}
